/* eslint-disable @typescript-eslint/no-unused-expressions */
import { Device, Theme, Tooltip } from '@livecontrol/core-ui';
import { Matrix } from '@livecontrol/scheduler/components';
import type { Location, User } from '@livecontrol/scheduler/model';
import type { ReactElement } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { RowLocation } from './row';

import G = Matrix.Grid;
import H = G.Header;

const Grid = styled(Matrix.Grid)<{ isHeaderHovered?: number }>`
  grid-row-gap: 0 !important;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 501px;

  &.grid{
    width: 1300px !important;
  }

  .border-top {
    border-top: 1px solid #e4e8f0;
  }

  .badge {
    font-size: 12px;
  }

  .bg-red {
    background-color: #ff505f;
  }

  .bg-admin {
    background-color: #ef9563;
  }

  .bg-purplue {
    background-color: #7b61ff;
  }

  .bg-green {
    background-color: #37b34a;
  }

  .bg-blue {
    background-color: #00c1c1;
  }

  .column:nth-child(2n + 0) {
    background-color: #f0f6ff;
  }

  .column:nth-child(2n + 1) {
    background-color: white;
  }

  .height-10px {
    height: 10px !important;
    padding: 0px !important;
  }

  .th {
    margin: 0 !important;
    padding: 20px 18px;
    height: 165px;
    font-weight: 600;
    font-size: var(--font-size-16px);
    color: var(--dark);
    z-index: 1000:
  }

  .form-group {
    flex: 0 0 0 !important;
  }

  .form-control {
    padding: 0px;
  }

  input[type='checkbox'] {
    cursor: pointer;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    border-radius: 5px;
    width: 16px;
    height: 16px;
    border: 1px solid #0d1438;
    outline: none;
  }

  input[type='checkbox']:checked {
    background-color: #0d6efd;
    border-color: #0d6efd;
  }

  input:checked[type='checkbox'] {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e");
  }

  ${({ isHeaderHovered }): string => {
    let style = '';

    if (isHeaderHovered) {
      // eslint-disable-next-line unicorn/prefer-spread
      style = style.concat(`
      li > .column:nth-child(${isHeaderHovered}) {
        border-left: 1px solid #2e5bff !important;
        border-right: 1px solid #2e5bff !important;
      }

      li > div:last-child  {
        border-top-right-radius: 0px !important;
        border-bottom-right-radius: 0px !important;
      }

      li:first-child > div:nth-child(${isHeaderHovered}) {
        border-top:  1px solid #2e5bff;
      }

      li:last-child > div:nth-child(${isHeaderHovered}) {
        border-bottom:  1px solid #2e5bff;
      }

    `);
    }

    return style;
  }};


`;

const Content = styled.div`

  .btn {
    padding: 0px !important;
    width: 34px !important;
    height: 34px !important;
    z-index: 1;
  }

  .next {
    right: 0 !important;
  }

  .arrowRotated {
    transform: rotate(180deg);
  }

  img {
    width: 12px;
  }
`;

const CellNoBorder = styled(Matrix.Grid.Row.Cell)`
  border: 0px solid transparent;
  justify-content: center;
  padding: 14px 0px;

  &:last-child {
    padding: 5px 0px !important;
  }
`;

interface Props {
  me: User;
  locations: Location[] | undefined;
  locationLoading: boolean;
}

export const AccountPermissions = ({ locations, locationLoading, me }: Props): ReactElement => {
  const [isHeaderHovered, setIsHeaderHovered] = useState<number>();
  const viewport = Theme.useViewport();

  return (
    <div className='d-flex mt-56px flex-row justify-content-center position-relative'>
      <Content className=''>
        <div
          className='position-absolute bg-white font-weight-bold'
          style={{
            width: Device.isMobile(viewport) ? '165px' : '300px',
            height: 165,
            zIndex: 1
          }}
          >
          <h1 className='font-size-28px'>Location Settings</h1>
          <div className='font-size-12px mb-28px'>Settings applied per location</div>
        </div>
      </Content>
      <Grid
        viewport={viewport}
        isHeaderHovered={isHeaderHovered}
        gridTemplateColumns={`${Device.isMobile(viewport) ? '165px' : '300px'} repeat(4, 165px)`}
        className='grid'
      >
        {/* Header */}
        <G.Row className='position-sticky d-flex' style={{ top: 0 }}>
        <H
        className='bg-white column font-weight-bold '
        viewport={viewport}
        style={{ left: 0, width: Device.isMobile(viewport) ? '165px' : '300px' }}
      >
        <span className='badge rounded-pill bg-primary text-white mb-8px' style={{ opacity: 0 }}>
          Event Schedule
        </span>

        <div style={{ width: Device.isMobile(viewport) ? '165px' : '300px', opacity: 0 }}>
          Event Schedule
        </div>
      </H>
      <H
        className='column text-center font-weight-bold '
        style={{ width: 165, maxWidth: 165, minWidth: 165 }}
        viewport={viewport}
        onMouseEnter={(): void => {
          setIsHeaderHovered(2);
        }}
        onMouseLeave={(): void => {
          setIsHeaderHovered(undefined);
        }}
      >
        <span className='badge rounded-pill bg-info text-white font-size-9px mb-8px'>
          Event Schedule
        </span>
        <div className='font-size-12px' style={{ width: 118, marginBottom: 43 }}>
          Manage & Edit
          <Tooltip
            backgroundColor='#0C3192'
            tip={
              <div>
                <div className='font-weight-bold font-size-11px'>
                  Access to create, edit and delete events. All users can view the event schedule.
                </div>
              </div>
            }
          >
            <img className='ml-4px' src='assets/icons/Info-blue.svg' alt='info' />
          </Tooltip>
        </div>
      </H>
      <H
        className='column text-center font-weight-bold'
        style={{ width: 165, maxWidth: 165, minWidth: 165 }}
        viewport={viewport}
        onMouseEnter={(): void => {
          setIsHeaderHovered(3);
        }}
        onMouseLeave={(): void => {
          setIsHeaderHovered(undefined);
        }}
      >
        <span className='badge rounded-pill bg-admin text-white font-size-9px mb-8px'>
          Content Library
        </span>
        <div className='font-size-12px' style={{ width: 118, marginBottom: 43 }}>
          View & Download
          <Tooltip
            backgroundColor='#0C3192'
            tip={
              <div>
                <div className='font-weight-bold font-size-11px'>
                  Access to only view and download past videos in the Content Library. Access to view and download individual and global stream analytics. 
                </div>
              </div>
            }
          >
            <img className='ml-4px' src='assets/icons/Info-blue.svg' alt='info' />
          </Tooltip>
        </div>
      </H>
      <H
        className='column text-center font-weight-bold'
        style={{ width: 165, maxWidth: 165, minWidth: 165 }}
        viewport={viewport}
        onMouseEnter={(): void => {
          setIsHeaderHovered(4);
        }}
        onMouseLeave={(): void => {
          setIsHeaderHovered(undefined);
        }}
      >
        <span className='badge rounded-pill font-size-9px bg-admin text-white mb-8px'>
          Content Library
        </span>
        <div className='font-size-12px' style={{ width: 118, marginBottom: 43 }}>
          Manage & Edit
          <Tooltip
            backgroundColor='#0C3192'
            tip={
              <div>
                <div className='font-weight-bold font-size-11px'>
                  Access to view, download, edit, and delete past videos in the Content Library. Access to view and download individual and global stream analytics.
                </div>
              </div>
            }
          >
            <img className='ml-4px' src='assets/icons/Info-blue.svg' alt='info' />
          </Tooltip>
        </div>
      </H>
      <H
        className='column text-center font-weight-bold'
        style={{ width: 165, maxWidth: 165, minWidth: 165 }}
        viewport={viewport}
        onMouseEnter={(): void => {
          setIsHeaderHovered(5);
        }}
        onMouseLeave={(): void => {
          setIsHeaderHovered(undefined);
        }}
      >
        <span className='badge rounded-pill bg-purplue text-white font-size-9px mb-8px'>
          Stream Settings
        </span>
        <div className='font-size-12px' style={{ width: 118, marginBottom: 43 }}>
          Production Notes
          <Tooltip
            backgroundColor='#0C3192'
            tip={
              <div>
                <div className='font-weight-bold font-size-11px'>
                  Access to update account Production Notes.
                </div>
              </div>
            }
          >
            <img className='ml-4px' src='assets/icons/Info-blue.svg' alt='info' />
          </Tooltip>
        </div>
      </H>
        </G.Row>

        <G.Row>
          <CellNoBorder className='column height-10px' style={{backgroundColor: 'transparent'}}/>
          <CellNoBorder className='column height-10px' />
          <CellNoBorder className='column height-10px' />
          <CellNoBorder className='column height-10px' />
        </G.Row>
        
        {!locationLoading &&
          locations?.map((location) => (
            <RowLocation location={location} key={location.id} me={me} />
          ))}
      </Grid>
    </div>
  );
};
